/// <reference types="google.maps" />
import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getGettingHereRoute } from "@/lib/getting-here.functions";

declare global {
  interface Window {
    google?: typeof google;
    __backlandGmapsInit?: () => void;
    __backlandGmapsPromise?: Promise<void>;
  }
}

const BROWSER_KEY = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY;
const CHANNEL = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_TRACKING_ID;

// Forest / earth-tone map style matching the Backland aesthetic
const MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#1a1a17" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#a89878" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a17" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#c9a961" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#2a2e24" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#7a8a6a" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#252820" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#2a2e24" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#3d342a" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9a8a6a" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#5a4a36" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#3d342a" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#1f2a2e" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#5a7080" }] },
];

function loadGoogleMaps(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.google?.maps) return Promise.resolve();
  if (window.__backlandGmapsPromise) return window.__backlandGmapsPromise;

  window.__backlandGmapsPromise = new Promise<void>((resolve, reject) => {
    if (!BROWSER_KEY) {
      reject(new Error("Missing Google Maps browser key"));
      return;
    }
    window.__backlandGmapsInit = () => resolve();
    const s = document.createElement("script");
    const params = new URLSearchParams({
      key: BROWSER_KEY,
      loading: "async",
      callback: "__backlandGmapsInit",
      libraries: "geometry",
    });
    if (CHANNEL) params.set("channel", CHANNEL);
    s.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    s.async = true;
    s.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(s);
  });
  return window.__backlandGmapsPromise;
}

export default function GettingHereMap() {
  const mapEl = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fetchRoute = useServerFn(getGettingHereRoute);
  const { data: route } = useQuery({
    queryKey: ["getting-here-route"],
    queryFn: () => fetchRoute(),
    staleTime: 1000 * 60 * 60 * 24,
  });

  useEffect(() => {
    if (!mapEl.current || !route) return;
    let cancelled = false;

    loadGoogleMaps()
      .then(() => {
        if (cancelled || !mapEl.current || !window.google) return;
        const g = window.google;

        const map = new g.maps.Map(mapEl.current, {
          center: { lat: 35.205, lng: -112.105 },
          zoom: 11,
          styles: MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          backgroundColor: "#1a1a17",
        });

        const bounds = new g.maps.LatLngBounds();

        if (route.polyline) {
          const path = g.maps.geometry.encoding.decodePath(route.polyline);
          new g.maps.Polyline({
            path,
            map,
            strokeColor: "#C9A961",
            strokeOpacity: 0.95,
            strokeWeight: 4,
          });
          path.forEach((p) => bounds.extend(p));
        }

        // Origin marker (Williams)
        const originPos = new g.maps.LatLng(route.origin.lat, route.origin.lng);
        new g.maps.Marker({
          position: originPos,
          map,
          title: "I-40 Exit 167",
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: "#C9A961",
            fillOpacity: 1,
            strokeColor: "#1a1a17",
            strokeWeight: 2,
          },
        });
        bounds.extend(originPos);

        // Destination marker (Backland)
        const destPos = new g.maps.LatLng(route.destination.lat, route.destination.lng);
        new g.maps.Marker({
          position: destPos,
          map,
          title: "Backland",
          icon: {
            path: "M 0,-12 C -6,-12 -10,-7 -10,-2 C -10,5 0,14 0,14 C 0,14 10,5 10,-2 C 10,-7 6,-12 0,-12 z",
            fillColor: "#C9A961",
            fillOpacity: 1,
            strokeColor: "#1a1a17",
            strokeWeight: 2,
            scale: 1.1,
            anchor: new g.maps.Point(0, 12),
          },
        });
        bounds.extend(destPos);

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });

    return () => {
      cancelled = true;
    };
  }, [route]);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-gold/30 bg-[#1a1a17]">
      <div ref={mapEl} className="w-full aspect-[4/3] md:aspect-[16/9]" />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-foreground/60 text-sm p-6 text-center">
          Map unavailable. Use the button below to open directions in Google Maps.
        </div>
      )}
    </div>
  );
}
